import type {
  ListProjectsResponse,
  ProjectDetailResponse,
  CreateProjectRequest,
  CreateProjectResponse,
  ProjectErrorResponse,
  GetSettingsResponse,
  UpdateSettingsRequest,
  UpdateSettingsResponse,
  UpdateSettingsConflictBody,
  CatalogResponse,
  ProjectSettingsView,
  ProviderCatalog,
  ListReposResponse,
  AttachRepoRequest,
  AttachRepoResponse,
  GetRepoOverridesResponse,
  UpdateRepoOverridesRequest,
  EffectiveConfigResponse,
  GetGuardrailsResponse,
  UpdateGuardrailsRequest,
  UpdateGuardrailsResponse,
  GuardrailsView,
  ListAuditResponse,
  RepoMembershipSummary,
  RepoOverridesView,
  ProvisionByokRequest,
  ProvisionByokResponse,
  SaveByokRequest,
  SaveByokResponse,
  ByokFullProvider,
  SwitchPlanRequest,
  SwitchPlanResponse,
  SwitchPlanConflictBody,
  PlanViolation,
} from '../../../../shared/types/projects';
import { getAuthToken } from '../auth';
import { authFetch } from './authFetch';
import { API_BASE } from './version';

interface ListProjectsQuery {
  limit?: number;
}

/** Backwards-compat: keep the old helper for the AuditTab CSV download
 *  which needs to set the Authorization header on an <a> blob fetch. */
function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}
void authHeaders;

async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as ProjectErrorResponse;
    if (body?.error?.message) return body.error.message;
  } catch {
    /* not JSON */
  }
  return fallback;
}

/** GET /api/v1/projects */
export async function fetchProjects(
  params: ListProjectsQuery = {},
): Promise<ListProjectsResponse> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.append('limit', String(params.limit));
  const url = `${API_BASE}/projects${qs.toString() ? `?${qs.toString()}` : ''}`;

  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, `Failed to fetch projects: ${response.statusText}`));
  }
  return (await response.json()) as ListProjectsResponse;
}

/** GET /api/v1/projects/:id */
export async function fetchProjectDetail(id: string): Promise<ProjectDetailResponse['data']> {
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    if (response.status === 404) throw new Error('NOT_FOUND');
    throw new Error(await readErrorMessage(response, `Failed to fetch project: ${response.statusText}`));
  }
  const body = (await response.json()) as ProjectDetailResponse;
  return body.data;
}

/** POST /api/v1/projects */
export async function createProject(req: CreateProjectRequest): Promise<CreateProjectResponse['data']> {
  const response = await authFetch(`${API_BASE}/projects`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!response.ok) {
    throw new Error(
      await readErrorMessage(
        response,
        response.status === 409 ? 'Project already exists' : `Failed to create project: ${response.statusText}`,
      ),
    );
  }
  const body = (await response.json()) as CreateProjectResponse;
  return body.data;
}

/**
 * DELETE /api/v1/projects/:id — soft-deletes (archives) the project.
 * The backend keeps the DDB rows so the project can be recovered; the
 * UI just stops showing it.
 */
export async function archiveProject(id: string): Promise<void> {
  const response = await authFetch(
    `${API_BASE}/projects/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, `Failed to archive project: ${response.statusText}`),
    );
  }
}

/** GET /api/v1/projects/:id/settings */
export async function fetchProjectSettings(id: string): Promise<ProjectSettingsView> {
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/settings`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    if (response.status === 404) throw new Error('NOT_FOUND');
    throw new Error(await readErrorMessage(response, `Failed to fetch settings: ${response.statusText}`));
  }
  const body = (await response.json()) as GetSettingsResponse;
  return body.data;
}

/**
 * Thrown when the server detects a version conflict during PUT settings.
 * Carries the server's current view so the caller can render a 3-way diff.
 */
export class SettingsConflictError extends Error {
  public readonly current: ProjectSettingsView;
  constructor(message: string, current: ProjectSettingsView) {
    super(message);
    this.name = 'SettingsConflictError';
    this.current = current;
  }
}

/** PUT /api/v1/projects/:id/settings */
export async function updateProjectSettings(
  id: string,
  req: UpdateSettingsRequest,
): Promise<ProjectSettingsView> {
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/settings`;
  const response = await authFetch(url, {
    method: 'PUT',
    body: JSON.stringify(req),
  });

  if (response.status === 409) {
    // Conflict body carries `current` so the UI can render a 3-way diff.
    let body: UpdateSettingsConflictBody | null = null;
    try {
      body = (await response.json()) as UpdateSettingsConflictBody;
    } catch {
      /* fall through */
    }
    throw new SettingsConflictError(
      body?.error.message ?? 'Version conflict — refresh and try again.',
      body?.error.current ?? { settings: {}, version: 0, updatedAt: null, updatedBy: null },
    );
  }

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, `Failed to save settings: ${response.statusText}`),
    );
  }
  const body = (await response.json()) as UpdateSettingsResponse;
  return body.data;
}

/**
 * POST /api/v1/projects/:id/byok — provision a customer-supplied API key
 * to AWS Secrets Manager and store the resulting ARN on the SETTINGS row.
 */
export async function provisionByok(
  id: string,
  req: ProvisionByokRequest,
): Promise<ProvisionByokResponse['data']> {
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/byok`;
  const response = await authFetch(url, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, `Failed to provision secret: ${response.statusText}`),
    );
  }
  const body = (await response.json()) as ProvisionByokResponse;
  return body.data;
}

/**
 * POST /api/v1/projects/:id/byok — save structured credentials for any of
 * the five full BYOK providers (anthropic, openai, bedrock, vertex, meta).
 * Shares the same route + handler as the legacy `provisionByok` (used by
 * SettingsTab) — the backend distinguishes the two request shapes by
 * whether `secret` or `credentials` is present. There never was a real PUT
 * handler for this route (frontend-only from commit de737d5 — every save
 * in the API Keys tab 404'd), so this now points at the endpoint that
 * actually exists and already works for Anthropic/OpenAI.
 */
export async function saveByokCredentials(
  id: string,
  req: SaveByokRequest,
): Promise<SaveByokResponse> {
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/byok`;
  const response = await authFetch(url, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, `Failed to save credentials: ${response.statusText}`),
    );
  }
  return (await response.json()) as SaveByokResponse;
}

/**
 * DELETE /api/v1/projects/:id/byok/:provider — remove a stored credential.
 * Returns 204 on success.
 */
export async function deleteByokCredentials(
  id: string,
  provider: ByokFullProvider,
): Promise<void> {
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/byok/${encodeURIComponent(provider)}`;
  const response = await authFetch(url, { method: 'DELETE' });
  if (response.status === 204) return;
  throw new Error(
    await readErrorMessage(response, `Failed to remove credentials: ${response.statusText}`),
  );
}

/** GET /api/v1/catalog/providers */
export async function fetchProviderCatalog(): Promise<ProviderCatalog> {
  const url = `${API_BASE}/catalog/providers`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, `Failed to fetch catalog: ${response.statusText}`));
  }
  const body = (await response.json()) as CatalogResponse;
  return body.data;
}

// ---------------------------------------------------------------------------
// Repos (FR-4) + effective config (FR-5)
// ---------------------------------------------------------------------------

export async function fetchProjectRepos(id: string): Promise<RepoMembershipSummary[]> {
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/repos`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load repos'));
  }
  const body = (await response.json()) as ListReposResponse;
  return body.data.repos;
}

export async function attachRepo(
  id: string,
  req: AttachRepoRequest,
): Promise<RepoMembershipSummary> {
  const response = await authFetch(`${API_BASE}/projects/${encodeURIComponent(id)}/repos`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to attach repo'));
  }
  const body = (await response.json()) as AttachRepoResponse;
  return body.data;
}

/** Thrown when DELETE returns REPO_IN_USE. */
export class RepoInUseError extends Error {
  public readonly blockingEpics: number[];
  constructor(message: string, blockingEpics: number[]) {
    super(message);
    this.name = 'RepoInUseError';
    this.blockingEpics = blockingEpics;
  }
}

export async function detachRepo(
  id: string,
  fullName: string,
  opts: { force?: boolean } = {},
): Promise<void> {
  const url = new URL(
    `${API_BASE}/projects/${encodeURIComponent(id)}/repos/${fullName}`,
    typeof window === 'undefined' ? 'http://localhost' : window.location.origin,
  );
  if (opts.force) url.searchParams.set('force', 'true');
  const response = await authFetch(url.toString(), {
    method: 'DELETE',
  });
  if (response.status === 204) return;
  if (response.status === 409) {
    let body: ProjectErrorResponse | null = null;
    try { body = (await response.json()) as ProjectErrorResponse; } catch { /* */ }
    throw new RepoInUseError(
      body?.error.message ?? 'Repo has in-flight epics',
      body?.error.blockingEpics ?? [],
    );
  }
  throw new Error(await readErrorMessage(response, 'Failed to detach repo'));
}

export async function fetchRepoOverrides(
  id: string,
  fullName: string,
): Promise<RepoOverridesView> {
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/repos/${fullName}/overrides`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    if (response.status === 404) throw new Error('NOT_FOUND');
    throw new Error(await readErrorMessage(response, 'Failed to load overrides'));
  }
  const body = (await response.json()) as GetRepoOverridesResponse;
  return body.data;
}

export async function updateRepoOverrides(
  id: string,
  fullName: string,
  req: UpdateRepoOverridesRequest,
): Promise<RepoOverridesView> {
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/repos/${fullName}/overrides`;
  const response = await authFetch(url, {
    method: 'PUT',
    body: JSON.stringify(req),
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to save overrides'));
  }
  const body = (await response.json()) as GetRepoOverridesResponse;
  return body.data;
}

export async function fetchEffectiveConfig(
  id: string,
  fullName: string,
): Promise<EffectiveConfigResponse['data']> {
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/repos/${fullName}/effective`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    if (response.status === 404) throw new Error('NOT_FOUND');
    throw new Error(await readErrorMessage(response, 'Failed to load effective config'));
  }
  const body = (await response.json()) as EffectiveConfigResponse;
  return body.data;
}

// ---------------------------------------------------------------------------
// Guardrails (FR-6)
// ---------------------------------------------------------------------------

export async function fetchProjectGuardrails(
  id: string,
  opts: { repo?: string } = {},
): Promise<GuardrailsView> {
  const qs = opts.repo ? `?repo=${encodeURIComponent(opts.repo)}` : '';
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/guardrails${qs}`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load guardrails'));
  }
  const body = (await response.json()) as GetGuardrailsResponse;
  return body.data;
}

export async function updateProjectGuardrails(
  id: string,
  req: UpdateGuardrailsRequest,
): Promise<GuardrailsView> {
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/guardrails`;
  const response = await authFetch(url, {
    method: 'PUT',
    body: JSON.stringify(req),
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to save guardrails'));
  }
  const body = (await response.json()) as UpdateGuardrailsResponse;
  return body.data;
}

// ---------------------------------------------------------------------------
// Audit log (FR-7)
// ---------------------------------------------------------------------------

export interface AuditQuery {
  actor?: string;
  changeType?: string;
  target?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

export async function fetchAuditEntries(
  id: string,
  q: AuditQuery = {},
): Promise<ListAuditResponse['data']> {
  const qs = new URLSearchParams();
  if (q.actor) qs.set('actor', q.actor);
  if (q.changeType) qs.set('changeType', q.changeType);
  if (q.target) qs.set('target', q.target);
  if (q.from) qs.set('from', q.from);
  if (q.to) qs.set('to', q.to);
  if (q.limit !== undefined) qs.set('limit', String(q.limit));
  if (q.cursor) qs.set('cursor', q.cursor);

  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/audit${qs.toString() ? `?${qs.toString()}` : ''}`;
  const response = await authFetch(url, { method: 'GET' });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load audit log'));
  }
  const body = (await response.json()) as ListAuditResponse;
  return body.data;
}

// ---------------------------------------------------------------------------
// Plan switching
// ---------------------------------------------------------------------------

/**
 * Thrown when the server returns 422 PLAN_VIOLATION.
 * Carries the list of limits that would be exceeded so the UI can display them.
 */
export class PlanViolationError extends Error {
  public readonly violations: PlanViolation[];
  constructor(message: string, violations: PlanViolation[]) {
    super(message);
    this.name = 'PlanViolationError';
    this.violations = violations;
  }
}

/** PUT /api/v1/projects/:id/plan */
export async function switchProjectPlan(
  id: string,
  req: SwitchPlanRequest,
): Promise<SwitchPlanResponse['data']> {
  const url = `${API_BASE}/projects/${encodeURIComponent(id)}/plan`;
  const response = await authFetch(url, {
    method: 'PUT',
    body: JSON.stringify(req),
  });

  if (response.status === 422) {
    let body: SwitchPlanConflictBody | null = null;
    try {
      body = (await response.json()) as SwitchPlanConflictBody;
    } catch { /* fall through */ }
    throw new PlanViolationError(
      body?.error.message ?? 'Plan downgrade would exceed current limits.',
      body?.error.violations ?? [],
    );
  }

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, `Failed to switch plan: ${response.statusText}`));
  }

  const body = (await response.json()) as SwitchPlanResponse;
  return body.data;
}

/** Returns the canonical CSV download URL with auth token interpolated. */
export function buildAuditCsvUrl(id: string, q: AuditQuery = {}): string {
  const qs = new URLSearchParams();
  if (q.actor) qs.set('actor', q.actor);
  if (q.changeType) qs.set('changeType', q.changeType);
  if (q.target) qs.set('target', q.target);
  if (q.from) qs.set('from', q.from);
  if (q.to) qs.set('to', q.to);
  return `${API_BASE}/projects/${encodeURIComponent(id)}/audit.csv${qs.toString() ? `?${qs.toString()}` : ''}`;
}
